import { useState, useEffect, useRef } from 'react';

const C = {
  bg:'#F0F6FB', white:'#FFFFFF', card:'#FFFFFF', border:'#C8DCF0',
  brand:'#3E4954', brandLight:'#D0E5F5', brandDark:'#293038',
  accent:'#FF4500', accentBg:'rgba(255,69,0,0.09)',
  text:'#1A2733', muted:'#5A7490', faint:'#9BB5CC',
  green:'#166534', greenBg:'#DCFCE7',
  amber:'#92400E', amberBg:'#FEF3C7',
  red:'#9F1239', redBg:'#FFE4E6',
};
const FONT = "'DM Sans', system-ui, sans-serif";

const GLOBAL = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
  html,body,#root,.dhiwise-code{background:#F0F6FB !important;color:#1A2733;font-family:${FONT};-webkit-font-smoothing:antialiased;}
  input,select{font-family:${FONT};background:#fff;color:#1A2733;border:1.5px solid #C8DCF0;border-radius:10px;padding:13px 16px;font-size:15px;width:100%;outline:none;transition:border-color 150ms,box-shadow 150ms;}
  input:focus,select:focus{border-color:#3E4954;box-shadow:0 0 0 3px #D0E5F5;}
  input::placeholder{color:#9BB5CC;}
  input:-webkit-autofill,input:-webkit-autofill:hover,input:-webkit-autofill:focus{-webkit-box-shadow:0 0 0 100px #fff inset !important;-webkit-text-fill-color:#1A2733 !important;}
  select option{background:#fff;color:#1A2733;}
  ::-webkit-scrollbar{width:4px;}::-webkit-scrollbar-thumb{background:#C8DCF0;border-radius:2px;}
  @keyframes fadeUp{from{opacity:0;transform:translateY(12px);}to{opacity:1;transform:none;}}
  .fade-up{animation:fadeUp 260ms ease forwards;}
  @keyframes spin{to{transform:rotate(360deg);}}
  .spin{display:inline-block;animation:spin 0.9s linear infinite;}
`;

// ── Helpers ───────────────────────────────────────────────────────────────────
const EV_KEY = 'zba_ev3', EV_EXP = 'zba_ev3_exp';
function saveEvent(ev){sessionStorage.setItem(EV_KEY,JSON.stringify(ev));sessionStorage.setItem(EV_EXP,String(Date.now()+86400000));}
function loadEvent(){const e=sessionStorage.getItem(EV_EXP);if(!e||Date.now()>+e){sessionStorage.removeItem(EV_KEY);return null;}try{return JSON.parse(sessionStorage.getItem(EV_KEY)||'null');}catch{return null;}}
function fmtDate(d){if(!d)return'—';const[y,m,day]=d.split('-');const wd=['So','Mo','Di','Mi','Do','Fr','Sa'][new Date(d).getDay()];return`${wd}, ${day}.${m}.${y}`;}

// ── Alert Modal ───────────────────────────────────────────────────────────────
function AlertModal({open,icon,title,message,onClose}){
  if(!open)return null;
  return(
    <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.45)',zIndex:2000,display:'flex',alignItems:'center',justifyContent:'center',padding:24}} onClick={e=>{if(e.target===e.currentTarget)onClose();}}>
      <div className="fade-up" style={{background:C.white,borderRadius:20,padding:32,maxWidth:340,width:'100%',textAlign:'center',boxShadow:'0 20px 60px rgba(0,0,0,0.25)',position:'relative'}}>
        <div style={{fontSize:52,marginBottom:16}}>{icon}</div>
        <h3 style={{fontSize:20,fontWeight:800,color:C.brand,marginBottom:12}}>{title}</h3>
        <p style={{fontSize:15,color:C.muted,lineHeight:1.6,marginBottom:28}}>{message}</p>
        <button type="button" onClick={onClose} style={{width:'100%',height:48,borderRadius:12,border:'none',background:C.brand,color:'white',fontFamily:FONT,fontSize:15,fontWeight:700,cursor:'pointer'}}>
          Verstanden
        </button>
      </div>
    </div>
  );
}

// ── Signature Modal ───────────────────────────────────────────────────────────
function SignatureModal({open,onClose,onConfirm}){
  const canvasRef=useRef(null);
  const painting=useRef(false);
  const last=useRef(null);
  const [drawn,setDrawn]=useState(false);
  const [agreed,setAgreed]=useState(false);

  useEffect(()=>{
    if(!open)return;
    setDrawn(false);setAgreed(false);
    setTimeout(()=>{
      const cv=canvasRef.current;if(!cv)return;
      const ctx=cv.getContext('2d');
      ctx.clearRect(0,0,cv.width,cv.height);
      ctx.strokeStyle=C.brand;ctx.lineWidth=2.8;ctx.lineCap='round';ctx.lineJoin='round';
      const gp=e=>{const r=cv.getBoundingClientRect(),t=e.touches?.[0];return{x:((t?.clientX??e.clientX)-r.left)*(cv.width/r.width),y:((t?.clientY??e.clientY)-r.top)*(cv.height/r.height)};};
      const dn=e=>{e.preventDefault();painting.current=true;last.current=gp(e);};
      const mv=e=>{e.preventDefault();if(!painting.current)return;const p=gp(e);ctx.beginPath();ctx.moveTo(last.current.x,last.current.y);ctx.lineTo(p.x,p.y);ctx.stroke();last.current=p;setDrawn(true);};
      const up=()=>{painting.current=false;};
      cv.addEventListener('mousedown',dn);cv.addEventListener('mousemove',mv);cv.addEventListener('mouseup',up);cv.addEventListener('mouseleave',up);
      cv.addEventListener('touchstart',dn,{passive:false});cv.addEventListener('touchmove',mv,{passive:false});cv.addEventListener('touchend',up);
    },60);
  },[open]);

  const clear=()=>{const cv=canvasRef.current;cv?.getContext('2d').clearRect(0,0,cv.width,cv.height);setDrawn(false);};
  const ok=drawn&&agreed;

  if(!open)return null;
  return(
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:2000,display:'flex',alignItems:'flex-end',justifyContent:'center'}} onClick={e=>{if(e.target===e.currentTarget)onClose();}}>
      <div className="fade-up" style={{background:C.white,borderRadius:'20px 20px 0 0',padding:'24px 20px 32px',width:'100%',maxWidth:600,boxShadow:'0 -12px 48px rgba(0,0,0,0.2)'}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20}}>
          <h3 style={{fontSize:19,fontWeight:800,color:C.brand}}>Einwilligung & Unterschrift</h3>
          <button type="button" onClick={onClose} style={{width:34,height:34,borderRadius:'50%',border:'none',background:C.bg,fontSize:20,cursor:'pointer',color:C.muted,display:'flex',alignItems:'center',justifyContent:'center'}}>×</button>
        </div>

        {/* DSGVO Text */}
        <div style={{background:'#F0F6FB',borderRadius:12,padding:'14px 16px',marginBottom:16,fontSize:13,color:C.muted,lineHeight:1.75,border:`1px solid ${C.border}`}}>
          Ich willige ein, dass die <strong style={{color:C.text}}>ZBA – Zentrale Brandschutztechnik-Akademie GmbH</strong> meine
          Kontaktdaten (Name, Telefon, E-Mail) zur Beratung über Qualifizierungsmaßnahmen
          speichert und verarbeitet. Rechtsgrundlage: <strong style={{color:C.text}}>Art. 6 Abs. 1 lit. a DSGVO</strong>.
          Die Einwilligung kann jederzeit widerrufen werden.
        </div>

        {/* Checkbox */}
        <div onClick={()=>setAgreed(a=>!a)} style={{display:'flex',alignItems:'flex-start',gap:12,cursor:'pointer',padding:'12px 14px',borderRadius:10,marginBottom:16,border:`1.5px solid ${agreed?C.brand:C.border}`,background:agreed?C.brandLight:C.white,transition:'all 130ms',userSelect:'none'}}>
          <div style={{width:22,height:22,borderRadius:6,flexShrink:0,marginTop:1,background:agreed?C.brand:C.white,border:`2px solid ${agreed?C.brand:C.border}`,display:'flex',alignItems:'center',justifyContent:'center',transition:'all 130ms'}}>
            {agreed&&<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3.5"><polyline points="20 6 9 17 4 12"/></svg>}
          </div>
          <div style={{fontSize:14,color:C.text,lineHeight:1.5}}>
            <strong>Ja, ich stimme der Datenschutzerklärung zu</strong> und bin damit einverstanden, dass ZBA mich kontaktiert.
          </div>
        </div>

        {/* Canvas */}
        <div style={{borderRadius:12,overflow:'hidden',border:`2px solid ${drawn?C.green:C.border}`,marginBottom:16,transition:'border-color 200ms'}}>
          <div style={{padding:'10px 16px',borderBottom:`1px solid ${C.border}`,background:drawn?C.greenBg:'#FAFCFF',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
            <span style={{fontSize:13,fontWeight:600,color:drawn?C.green:C.muted}}>
              {drawn?'✓ Unterschrift erfasst':'✍ Hier mit Finger oder Stift unterschreiben'}
            </span>
            {drawn&&<button type="button" onClick={clear} style={{padding:'4px 12px',border:`1px solid ${C.border}`,borderRadius:6,background:C.white,cursor:'pointer',fontSize:12,fontFamily:FONT,color:C.muted}}>Löschen</button>}
          </div>
          <canvas ref={canvasRef} width={560} height={180} style={{width:'100%',height:180,display:'block',touchAction:'none',cursor:'crosshair',background:'#FEFEFE'}}/>
        </div>

        {!ok&&<div style={{textAlign:'center',fontSize:13,color:C.faint,marginBottom:12}}>{!agreed?'☝ Bitte Checkbox oben ankreuzen':!drawn?'☝ Bitte unterschreiben':''}</div>}

        <div style={{display:'grid',gridTemplateColumns:'1fr 2fr',gap:10}}>
          <button type="button" onClick={onClose} style={{height:50,borderRadius:12,border:`1.5px solid ${C.border}`,background:C.white,fontFamily:FONT,fontSize:14,cursor:'pointer',color:C.muted}}>Abbrechen</button>
          <button type="button" onClick={()=>onConfirm(canvasRef.current?.toDataURL('image/png'))} disabled={!ok}
            style={{height:50,borderRadius:12,border:'none',background:ok?C.brand:C.border,color:'white',fontFamily:FONT,fontSize:15,fontWeight:700,cursor:ok?'pointer':'not-allowed',transition:'background 150ms'}}>
            ✓ Einwilligung bestätigen
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Calendar ──────────────────────────────────────────────────────────────────
const MONTHS=['Januar','Februar','März','April','Mai','Juni','Juli','August','September','Oktober','November','Dezember'];
const WD=['Mo','Di','Mi','Do','Fr','Sa','So'];
function fmtD(d){return`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;}
function buildGrid(y,m){const f=new Date(y,m,1);let dow=f.getDay()-1;if(dow<0)dow=6;const days=new Date(y,m+1,0).getDate();const cells=Array(dow).fill(null);for(let d=1;d<=days;d++)cells.push(new Date(y,m,d));return cells;}

// ── German public holidays (NRW — covers Wülfrath location) ─────────────────
function getGermanHolidays(year) {
  // Easter Sunday calculation (Gaussian algorithm)
  const a = year % 19, b = Math.floor(year/100), k = Math.floor(b/4);
  const p = Math.floor((b+8)/25), q = Math.floor((b-p+1)/3);
  const M = (15+b-k-q)%30, N = (4+b-k)%7;
  const d = (19*a+M)%30, e = (2*k+4*N+6*d+N)%7;
  let easterDay = 22+d+e;
  let easterMonth = 3;
  if(easterDay > 31){ easterDay -= 31; easterMonth = 4; }
  if(d===29&&e===6){ easterDay=19; easterMonth=4; }
  if(d===28&&e===6&&a>10){ easterDay=18; easterMonth=4; }
  const easter = new Date(year, easterMonth-1, easterDay);

  const add = (date, days) => { const d=new Date(date); d.setDate(d.getDate()+days); return d; };
  const fmt = d => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;

  const holidays = new Set([
    `${year}-01-01`, // Neujahr
    `${year}-05-01`, // Tag der Arbeit
    `${year}-10-03`, // Tag der deutschen Einheit
    `${year}-11-01`, // Allerheiligen (NRW)
    `${year}-12-25`, // 1. Weihnachtstag
    `${year}-12-26`, // 2. Weihnachtstag
    fmt(add(easter,-2)),  // Karfreitag
    fmt(easter),          // Ostersonntag
    fmt(add(easter,1)),   // Ostermontag
    fmt(add(easter,39)),  // Christi Himmelfahrt
    fmt(add(easter,49)),  // Pfingstsonntag
    fmt(add(easter,50)),  // Pfingstmontag
    fmt(add(easter,60)),  // Fronleichnam (NRW)
  ]);
  return holidays;
}


function getBookable(){
  const t=new Date();t.setHours(0,0,0,0);
  const ds=[];let n=0,c=new Date(t);c.setDate(c.getDate()+1);
  const holidays=new Set([...getGermanHolidays(c.getFullYear()),...getGermanHolidays(c.getFullYear()+1)]);
  while(n<14){
    const f=fmtD(c);
    if(c.getDay()!==0&&c.getDay()!==6&&!holidays.has(f)){ds.push(new Date(c));n++;}
    c.setDate(c.getDate()+1);
  }
  return ds;
}

function EventCalendar({selected,onSelect,type}){
  const bookable=getBookable();
  const [unavail,setUnavail]=useState(new Set());
  const [ready,setReady]=useState(false);
  const minD=bookable[0],maxD=bookable[bookable.length-1];
  const [vy,setVy]=useState(minD.getFullYear());
  const [vm,setVm]=useState(minD.getMonth());

  useEffect(()=>{
    setReady(false);
    fetch(`/api/free-busy?startDate=${fmtD(minD)}&endDate=${fmtD(maxD)}&type=${type||'onsite'}&source=regular`)
      .then(r=>r.json()).then(data=>{
        const u=new Set();
        for(const day of(data.days||[])){if(!day.slots?.some(s=>!s.conflict))u.add(day.date);}
        setUnavail(u);
        const first=bookable.find(d=>!u.has(fmtD(d)));
        if(first){setVy(first.getFullYear());setVm(first.getMonth());}
        setReady(true);
      }).catch(()=>setReady(true));
  },[type]);

  const cells=buildGrid(vy,vm);
  const holidays=new Set([...getGermanHolidays(vy),...getGermanHolidays(vy+1)]);
  const isBook=d=>d&&d.getDay()!==0&&d.getDay()!==6&&!holidays.has(fmtD(d))&&bookable.some(b=>b.toDateString()===d.toDateString())&&!unavail.has(fmtD(d));
  const isSel=d=>d&&fmtD(d)===selected;
  const canPrev=new Date(vy,vm)>new Date(minD.getFullYear(),minD.getMonth());
  const canNext=new Date(vy,vm)<new Date(maxD.getFullYear(),maxD.getMonth());

  if(!ready)return<div style={{textAlign:'center',padding:32,color:C.muted}}><span className="spin">⟳</span> Verfügbarkeit laden...</div>;

  return(
    <div style={{borderRadius:14,overflow:'hidden',border:`1.5px solid ${C.border}`,boxShadow:'0 2px 12px rgba(62,73,84,0.08)'}}>
      <div style={{background:C.brand,padding:'14px 20px',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
        <button type="button" onClick={()=>canPrev&&(vm===0?(setVm(11),setVy(y=>y-1)):setVm(m=>m-1))} disabled={!canPrev}
          style={{width:36,height:36,borderRadius:8,border:'none',background:'rgba(255,255,255,0.15)',color:'white',cursor:canPrev?'pointer':'default',opacity:canPrev?1:0.3,fontSize:20,display:'flex',alignItems:'center',justifyContent:'center'}}>‹</button>
        <span style={{color:'white',fontWeight:700,fontSize:15}}>{MONTHS[vm]} {vy}</span>
        <button type="button" onClick={()=>canNext&&(vm===11?(setVm(0),setVy(y=>y+1)):setVm(m=>m+1))} disabled={!canNext}
          style={{width:36,height:36,borderRadius:8,border:'none',background:'rgba(255,255,255,0.15)',color:'white',cursor:canNext?'pointer':'default',opacity:canNext?1:0.3,fontSize:20,display:'flex',alignItems:'center',justifyContent:'center'}}>›</button>
      </div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',background:C.brandLight}}>
        {WD.map(d=><div key={d} style={{textAlign:'center',fontSize:10,fontWeight:700,color:C.brand,padding:'8px 0',textTransform:'uppercase',letterSpacing:'0.05em'}}>{d}</div>)}
      </div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',background:C.white,padding:8,gap:3}}>
        {cells.map((date,i)=>{
          if(!date)return<div key={i}/>;
          const book=isBook(date),sel=isSel(date);
          return(
            <button key={i} type="button" disabled={!book} onClick={()=>book&&onSelect(fmtD(date))}
              style={{aspectRatio:'1',width:'100%',display:'flex',alignItems:'center',justifyContent:'center',borderRadius:'50%',fontSize:13,fontFamily:FONT,fontWeight:sel?700:book?600:400,background:sel?C.accent:'transparent',color:sel?'white':book?C.text:C.faint,border:`2px solid ${sel?C.accent:'transparent'}`,cursor:book?'pointer':'default',transform:sel?'scale(1.1)':'scale(1)',boxShadow:sel?'0 3px 12px rgba(255,69,0,0.3)':'none',transition:'all 130ms',outline:'none'}}
              onMouseEnter={e=>{if(book&&!sel)e.currentTarget.style.background=C.brandLight;}}
              onMouseLeave={e=>{if(!sel)e.currentTarget.style.background='transparent';}}>
              {date.getDate()}
            </button>
          );
        })}
      </div>
      <div style={{textAlign:'center',padding:'8px',fontSize:11,color:C.muted,borderTop:`1px solid ${C.border}`,background:'#F8FBFF'}}>
        Mo–Fr · {minD.toLocaleDateString('de-DE')} – {maxD.toLocaleDateString('de-DE')}
      </div>
    </div>
  );
}

// ── Step 0: Event selection ───────────────────────────────────────────────────
function StepEvent({onSelect}){
  const [events,setEvents]=useState([]);
  const [loading,setLoading]=useState(true);
  const [search,setSearch]=useState('');
  const dbg=new URLSearchParams(window.location.search).get('debug_adm');
  const load=(q='')=>{setLoading(true);const p=new URLSearchParams({search:q});if(dbg)p.set('debug_adm',dbg);fetch(`/api/events?${p}`).then(r=>r.json()).then(d=>{setEvents(d.events||[]);setLoading(false);}).catch(()=>setLoading(false));};
  useEffect(()=>load(),[]);
  return(
    <div className="fade-up">
      <div style={{textAlign:'center',marginBottom:32}}>
        <div style={{fontSize:48,marginBottom:12}}>📋</div>
        <h1 style={{fontSize:26,fontWeight:800,color:C.brand,marginBottom:8}}>Event auswählen</h1>
        <p style={{color:C.muted,fontSize:15}}>Welche Veranstaltung findet heute statt?</p>
      </div>
      <div style={{position:'relative',marginBottom:16}}>
        <input value={search} autoComplete="off" data-lpignore="true" onChange={e=>{setSearch(e.target.value);load(e.target.value);}} placeholder="Event suchen..." style={{paddingLeft:42}}/>
        <span style={{position:'absolute',left:14,top:'50%',transform:'translateY(-50%)',color:C.faint}}>🔍</span>
      </div>
      {loading?<div style={{textAlign:'center',padding:48,color:C.muted}}><span className="spin">⟳</span></div>:
       events.length===0?<div style={{textAlign:'center',padding:48,background:C.white,borderRadius:16,border:`1px solid ${C.border}`,color:C.muted}}>Keine Events im Zeitraum.<br/><small>Tipp: <code>?debug_adm=1</code> für alle</small></div>:
       events.map(ev=>(
         <button key={ev.id} onClick={()=>onSelect(ev)} type="button"
           style={{width:'100%',padding:'18px 20px',borderRadius:14,cursor:'pointer',background:C.white,border:`1.5px solid ${C.border}`,marginBottom:10,fontFamily:FONT,textAlign:'left',display:'flex',justifyContent:'space-between',alignItems:'center',transition:'all 140ms'}}
           onMouseEnter={e=>{e.currentTarget.style.borderColor=C.brand;e.currentTarget.style.background=C.brandLight;}}
           onMouseLeave={e=>{e.currentTarget.style.borderColor=C.border;e.currentTarget.style.background=C.white;}}>
           <div>
             <div style={{fontSize:16,fontWeight:700,color:C.text,marginBottom:4}}>{ev.name}</div>
             <div style={{fontSize:13,color:C.muted}}>{ev.location&&`📍 ${ev.location} · `}{fmtDate(ev.event_date)}</div>
           </div>
           <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={C.faint} strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
         </button>
       ))
      }
    </div>
  );
}

// ── Step 1: TN Form ───────────────────────────────────────────────────────────
function StepTN({event,onSubmit}){
  const [form,setForm]=useState({vorname:'',nachname:'',telefon:'',email:'',consultationType:'onsite',sprachniveau:'',fuehrerschein:false,auto:false,kundennummer:'',showOptional:false,termin_date:'',termin_time:'',termin_berater:''});
  const [noEmail,setNoEmail]=useState(false);
  const [noTelefon,setNoTelefon]=useState(false);
  const [skipTermin,setSkipTermin]=useState(false);
  const [slots,setSlots]=useState([]);
  const [loadingSlots,setLoadingSlots]=useState(false);
  const [loading,setLoading]=useState(false);
  const [signed,setSigned]=useState(false);
  // Refs to always have current values at validation time (avoids stale closure)
  const terminRef = useRef({date:'',time:'',berater:''});
  const [sigData,setSigData]=useState(null);
  const [showSig,setShowSig]=useState(false);
  const [alert,setAlert]=useState(null); // {icon,title,message}
  const set=(k,v)=>{
    setForm(p=>({...p,[k]:v}));
    if(k==='termin_date')terminRef.current.date=v;
    if(k==='termin_time')terminRef.current.time=v;
    if(k==='termin_berater')terminRef.current.berater=v;
  };

  useEffect(()=>{
    if(!form.termin_date)return;
    setLoadingSlots(true);setSlots([]);
    fetch(`/api/slots?date=${form.termin_date}&type=${form.consultationType}&source=regular`)
      .then(r=>r.json()).then(d=>{setSlots(d.slots||[]);setLoadingSlots(false);}).catch(()=>setLoadingSlots(false));
  },[form.termin_date,form.consultationType]);

  const handleWeiter=()=>{
    // Validate required fields first
    if(!form.vorname.trim()||!form.nachname.trim()){
      setAlert({icon:'📝',title:'Felder ausfüllen',message:'Bitte Vorname und Nachname ausfüllen.'});return;
    }
    if(!noTelefon&&!form.telefon.trim()){
      setAlert({icon:'📞',title:'Telefon fehlt',message:'Bitte Telefonnummer eingeben oder "Keine Telefonnummer" markieren.'});return;
    }
    if(!noEmail&&!form.email.trim()){setAlert({icon:'📧',title:'E-Mail fehlt',message:'Bitte E-Mail eingeben oder "Keine E-Mail" markieren.'});return;}
    if(!noEmail&&form.email.trim()&&!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)){
      setAlert({icon:'📧',title:'E-Mail ungültig',message:'Bitte eine gültige E-Mail-Adresse eingeben.'});return;
    }
    // Check termin (skip if "ohne Termin")
    if(!skipTermin){
      const td=terminRef.current.date||form.termin_date;
      const tt=terminRef.current.time||form.termin_time;
      if(!td){
        setAlert({icon:'📅',title:'Datum fehlt',message:'Bitte wähle im Kalender einen Tag aus.'});return;
      }
      if(!tt){
        setAlert({icon:'⏰',title:'Uhrzeit fehlt',message:'Bitte wähle eine der angezeigten Uhrzeiten aus.'});return;
      }
    }
    // Open signature modal
    setShowSig(true);
  };

  const handleSigConfirm=async(dataUrl)=>{
    setShowSig(false);setSigned(true);setSigData(dataUrl);
    setLoading(true);
    try{
      const r=await fetch('/api/events/lead',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({event_id:event.id,...form,telefon:noTelefon?'':form.telefon,email:noEmail?'':form.email,no_email:noEmail,no_telefon:noTelefon,signature_given:true,signature_data:dataUrl,consent_timestamp:new Date().toISOString(),termin_date:skipTermin?'':form.termin_date,termin_time:skipTermin?'':form.termin_time,termin_berater:skipTermin?'':form.termin_berater,consultationType:form.consultationType,skip_termin:skipTermin})});
      const d=await r.json();
      if(d.success)onSubmit({...form,lead_id:d.lead_id,pd_deal_id:d.pd_deal_id});
      else setAlert({icon:'❌',title:'Fehler',message:d.error||'Unbekannter Fehler. Bitte nochmal versuchen.'});
    }catch{setAlert({icon:'📶',title:'Verbindungsfehler',message:'Bitte Internetverbindung prüfen und nochmal versuchen.'});}
    finally{setLoading(false);}
  };

  return(
    <div className="fade-up" style={{position:'relative'}}>
      {loading && <div style={{position:'absolute',inset:0,background:'rgba(240,246,251,0.85)',zIndex:50,borderRadius:12,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:16}}>
        <span className="spin" style={{fontSize:32}}>⟳</span>
        <div style={{fontSize:15,fontWeight:700,color:C.brand}}>Wird gespeichert...</div>
        <div style={{fontSize:13,color:C.muted}}>Bitte warten</div>
      </div>}
      <AlertModal open={!!alert} icon={alert?.icon} title={alert?.title} message={alert?.message} onClose={()=>setAlert(null)}/>
      <SignatureModal open={showSig} onClose={()=>setShowSig(false)} onConfirm={handleSigConfirm}/>

      {/* Hidden honeypot */}
      <div style={{position:'absolute',opacity:0,height:0,overflow:'hidden',pointerEvents:'none'}} aria-hidden="true">
        <input type="text" name="username" tabIndex={-1} readOnly/><input type="email" name="email" tabIndex={-1} readOnly/><input type="password" name="password" tabIndex={-1} readOnly/>
      </div>

      <div style={{marginBottom:24}}>
        <div style={{fontSize:11,fontWeight:700,color:C.accent,textTransform:'uppercase',letterSpacing:'0.08em',marginBottom:6}}>{event.name}</div>
        <h2 style={{fontSize:22,fontWeight:800,color:C.brand}}>Interessentenanmeldung</h2>
        <p style={{fontSize:13,color:C.muted,marginTop:4}}>Bitte alle Pflichtfelder ausfüllen.</p>
      </div>

      {/* Progress dots */}
      <div style={{display:'flex',gap:6,marginBottom:28}}>
        {[0,1].map(i=><div key={i} style={{flex:1,height:4,borderRadius:2,background:i===0?C.accent:C.border}}/>)}
      </div>

      {/* Name */}
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
        <div style={{marginBottom:18}}>
          <div style={{fontSize:12,fontWeight:700,color:C.brand,textTransform:'uppercase',letterSpacing:'0.07em',marginBottom:7}}>Vorname <span style={{color:C.accent}}>*</span></div>
          <input autoComplete="off" data-lpignore="true" data-form-type="other" data-1p-ignore="true" name="zba_vorname" autoCorrect="off" spellCheck="false" value={form.vorname} onChange={e=>set('vorname',e.target.value)}/>
        </div>
        <div style={{marginBottom:18}}>
          <div style={{fontSize:12,fontWeight:700,color:C.brand,textTransform:'uppercase',letterSpacing:'0.07em',marginBottom:7}}>Nachname <span style={{color:C.accent}}>*</span></div>
          <input autoComplete="off" data-lpignore="true" data-form-type="other" data-1p-ignore="true" name="zba_nachname" autoCorrect="off" spellCheck="false" value={form.nachname} onChange={e=>set('nachname',e.target.value)}/>
        </div>
      </div>
      <div style={{marginBottom:18}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:7}}>
          <div style={{fontSize:12,fontWeight:700,color:C.brand,textTransform:'uppercase',letterSpacing:'0.07em'}}>Telefon {!noTelefon&&<span style={{color:C.accent}}>*</span>}</div>
          <label style={{fontSize:11,color:C.muted,cursor:'pointer',display:'flex',alignItems:'center',gap:4,userSelect:'none'}}>
            <input type="checkbox" checked={noTelefon} onChange={e=>{setNoTelefon(e.target.checked);if(e.target.checked)set('telefon','');}} style={{width:14,height:14}}/>
            Nicht vorhanden
          </label>
        </div>
        <input type="tel" autoComplete="off" data-lpignore="true" data-form-type="other" data-1p-ignore="true" name="zba_telefon" inputMode="tel" placeholder="+49 152 ..." value={form.telefon} onChange={e=>set('telefon',e.target.value)} disabled={noTelefon} style={noTelefon?{opacity:0.4,background:'#f0f0f0'}:{}}/>
      </div>
      <div style={{marginBottom:18}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:7}}>
          <div style={{fontSize:12,fontWeight:700,color:C.brand,textTransform:'uppercase',letterSpacing:'0.07em'}}>E-Mail {!noEmail&&<span style={{color:C.accent}}>*</span>}</div>
          <label style={{fontSize:11,color:C.muted,cursor:'pointer',display:'flex',alignItems:'center',gap:4,userSelect:'none'}}>
            <input type="checkbox" checked={noEmail} onChange={e=>{setNoEmail(e.target.checked);if(e.target.checked)set('email','');}} style={{width:14,height:14}}/>
            Keine E-Mail vorhanden
          </label>
        </div>
        <input id="zba_notapassword" type="email" autoComplete="new-password" data-lpignore="true" data-1p-ignore="true" data-bwignore="true" data-form-type="other" name={`email_${Math.random()}`} value={form.email} onChange={e=>set('email',e.target.value)} disabled={noEmail} style={noEmail?{opacity:0.4,background:'#f0f0f0'}:{}}/>
      </div>

      {/* Optional — always visible */}
      <div style={{marginBottom:4}}>
        <div style={{fontSize:12,fontWeight:700,color:C.brand,textTransform:'uppercase',letterSpacing:'0.07em',marginBottom:10}}>Weitere Angaben</div>
          <div style={{marginBottom:18}}>
            <div style={{fontSize:12,fontWeight:700,color:C.brand,textTransform:'uppercase',letterSpacing:'0.07em',marginBottom:8}}>Fahrzeug & Führerschein</div>
            {[['fuehrerschein','🚗 Führerschein Klasse B'],['auto','🚙 Eigenes Fahrzeug']].map(([k,label])=>(
              <div key={k} onClick={()=>set(k,!form[k])} style={{display:'flex',alignItems:'center',gap:12,cursor:'pointer',padding:'13px 16px',borderRadius:10,marginBottom:8,userSelect:'none',border:`1.5px solid ${form[k]?C.brand:C.border}`,background:form[k]?C.brandLight:C.white,transition:'all 130ms'}}>
                <div style={{width:22,height:22,borderRadius:6,flexShrink:0,background:form[k]?C.brand:C.white,border:`2px solid ${form[k]?C.brand:C.border}`,display:'flex',alignItems:'center',justifyContent:'center',transition:'all 130ms'}}>
                  {form[k]&&<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3.5"><polyline points="20 6 9 17 4 12"/></svg>}
                </div>
                <span style={{fontSize:15,fontWeight:600,color:C.text}}>{label}</span>
              </div>
            ))}
            <div style={{fontSize:12,fontWeight:700,color:C.brand,textTransform:'uppercase',letterSpacing:'0.07em',marginBottom:7,marginTop:14}}>Deutschkenntnisse</div>
            <select autoComplete="off" value={form.sprachniveau} onChange={e=>set('sprachniveau',e.target.value)}>
              <option value="">Nicht angegeben</option>
              {['A1','A2','B1','B2','C1','C2'].map(l=><option key={l}>{l}</option>)}
            </select>
          </div>

          <div style={{marginBottom:18}}>
            <div style={{fontSize:12,fontWeight:700,color:C.brand,textTransform:'uppercase',letterSpacing:'0.07em',marginBottom:7}}>Kundennummer Jobcenter</div>
            <input autoComplete="off" data-lpignore="true" placeholder="z.B. 123A456789" value={form.kundennummer} onChange={e=>set('kundennummer',e.target.value)}/>
          </div>
      </div>

      {/* ── Termin Section ── */}
      {!skipTermin && (
      <div style={{background:C.brandLight,borderRadius:16,padding:20,marginBottom:20,border:`2px solid ${C.brand}`}}>
        <div style={{fontSize:13,fontWeight:700,color:C.brand,marginBottom:14,display:'flex',alignItems:'center',gap:8}}>
          <span style={{fontSize:18}}>📅</span> Beratungstermin vereinbaren
        </div>

        {/* Always Vor-Ort */}

        <div style={{padding:'10px 14px',background:C.white,borderRadius:10,border:`1px solid ${C.border}`,fontSize:13,color:C.muted,marginBottom:16,display:'flex',gap:8}}>
          <span>📍</span><span><strong style={{color:C.text}}>ZBA Academy</strong> · Kruppstraße 41–45, 42489 Wülfrath</span>
        </div>

        <div style={{marginBottom:12}}>
          <div style={{fontSize:12,fontWeight:700,color:C.brand,textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:10}}>Datum wählen</div>
          <EventCalendar selected={form.termin_date} type={form.consultationType} onSelect={d=>{set('termin_date',d);set('termin_time','');set('termin_berater','');}}/>
        </div>

        {form.termin_date&&(
          <div className="fade-up">
            <div style={{fontSize:12,fontWeight:700,color:C.brand,textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:10,marginTop:16}}>Uhrzeit</div>
            {loadingSlots?<div style={{textAlign:'center',padding:20,color:C.muted}}><span className="spin">⟳</span> Lade Zeiten...</div>:
             slots.length>0?(
               <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:8}}>
                 {slots.map((s,i)=>{const sel=form.termin_time===s.time&&form.termin_berater===(s.berater?.email||s.berater_email||'');return(
                   <button key={i} type="button" onClick={()=>{set('termin_time',s.time);set('termin_berater',s.berater?.email||s.berater_email||'');}}
                     style={{padding:'12px 8px',borderRadius:10,fontFamily:FONT,cursor:'pointer',textAlign:'center',transition:'all 130ms',background:sel?C.accent:C.white,border:`2px solid ${sel?C.accent:C.border}`,boxShadow:sel?'0 3px 10px rgba(255,69,0,0.25)':'none',color:sel?'white':C.text}}>
                     <div style={{fontSize:16,fontWeight:800}}>{s.time}</div>
                     <div style={{fontSize:10,color:sel?'rgba(255,255,255,0.75)':C.muted,marginTop:2}}>Uhr</div>
                   </button>
                 );})}
               </div>
             ):<div style={{padding:'14px 16px',background:C.amberBg,borderRadius:10,color:C.amber,fontSize:13}}>⚠ Keine freien Termine an diesem Tag — anderen Tag wählen</div>
            }
          </div>
        )}

        {form.termin_date&&form.termin_time&&(
          <div style={{marginTop:14,padding:'10px 14px',background:C.greenBg,borderRadius:10,fontSize:13,color:C.green,fontWeight:600,display:'flex',gap:8}}>
            ✓ Termin: {fmtDate(form.termin_date)} um {form.termin_time} Uhr
          </div>
        )}
      </div>
      )}

      {/* Skip termin link */}
      {!skipTermin ? (
        <div style={{textAlign:'center',marginBottom:16}}>
          <button type="button" onClick={()=>{setSkipTermin(true);set('termin_date','');set('termin_time','');set('termin_berater','');}}
            style={{background:'none',border:'none',color:C.muted,fontSize:12,cursor:'pointer',fontFamily:FONT,textDecoration:'underline',opacity:0.7}}>
            Ohne Termin fortfahren
          </button>
        </div>
      ) : (
        <div style={{padding:'12px 16px',background:'#FEF3C7',borderRadius:10,marginBottom:16,fontSize:13,color:'#92400E',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
          <span>📞 Kein Termin — wir melden uns telefonisch</span>
          <button type="button" onClick={()=>setSkipTermin(false)}
            style={{background:'none',border:'none',color:'#92400E',fontSize:12,cursor:'pointer',fontFamily:FONT,textDecoration:'underline'}}>
            Doch Termin wählen
          </button>
        </div>
      )}

      <button type="button" onClick={handleWeiter} disabled={loading}
        style={{width:'100%',height:54,borderRadius:12,border:'none',background:loading?C.border:C.accent,color:'white',fontFamily:FONT,fontSize:17,fontWeight:800,cursor:loading?'not-allowed':'pointer',transition:'all 150ms',display:'flex',alignItems:'center',justifyContent:'center',gap:8,boxShadow:'0 4px 20px rgba(255,69,0,0.25)'}}>
        {loading?<><span className="spin">⟳</span> Wird gespeichert...</>:'Weiter →'}
      </button>
    </div>
  );
}

// ── Step 2: Handover ──────────────────────────────────────────────────────────
function StepHandover({lead,onContinue,onSkip}){
  const [n,setN]=useState(30);
  useEffect(()=>{if(n<=0){return;}const t=setTimeout(()=>setN(c=>c-1),1000);return()=>clearTimeout(t);},[n]);
  const circ=2*Math.PI*36,prog=(n/30)*circ;
  return(
    <div className="fade-up" style={{textAlign:'center'}}>
      <div style={{fontSize:56,marginBottom:16}}>✅</div>
      <h2 style={{fontSize:24,fontWeight:800,color:C.brand,marginBottom:10}}>Danke, {lead.vorname}!</h2>
      <p style={{color:C.muted,fontSize:15,lineHeight:1.7,marginBottom:8}}>Ihre Daten wurden gespeichert.</p>
      {lead.termin_date&&lead.termin_time?<p style={{color:C.green,fontWeight:700,fontSize:15,marginBottom:8}}>📅 Termin: {fmtDate(lead.termin_date)} um {lead.termin_time} Uhr</p>:<p style={{color:'#92400E',fontWeight:600,fontSize:14,marginBottom:8,background:'#FEF3C7',padding:'8px 14px',borderRadius:8,display:'inline-block'}}>📞 Wir melden uns telefonisch</p>}
      <p style={{color:C.muted,fontSize:14,marginBottom:36}}>Bitte geben Sie das Tablet an den Dozenten weiter.</p>
      <svg width="100" height="100" viewBox="0 0 100 100" style={{marginBottom:24,display:'block',margin:'0 auto 24px'}}>
        <circle cx="50" cy="50" r="36" fill="none" stroke={C.border} strokeWidth="6"/>
        <circle cx="50" cy="50" r="36" fill="none" stroke={C.accent} strokeWidth="6" strokeDasharray={`${prog} ${circ}`} strokeLinecap="round" transform="rotate(-90 50 50)" style={{transition:'stroke-dasharray 1s linear'}}/>
        <text x="50" y="57" textAnchor="middle" fill={C.brand} style={{fontSize:26,fontWeight:800,fontFamily:FONT}}>{n}</text>
      </svg>
      <p style={{color:C.faint,fontSize:13,marginBottom:24}}>Automatisch weiter in {n}s</p>
      <button type="button" onClick={onContinue} style={{width:'100%',height:52,borderRadius:12,border:'none',background:C.brand,color:'white',fontFamily:FONT,fontSize:15,fontWeight:700,cursor:'pointer'}}>✎ Weiter — Dozenten-Infos eingeben</button>
      <p style={{fontSize:12,color:C.faint,textAlign:'center',marginTop:10}}>Bitte Tablet an den Dozenten weitergeben</p>
    </div>
  );
}

// ── Step 3: Dozent ────────────────────────────────────────────────────────────
function StepDozent({event,lead,onDone}){
  const [form,setForm]=useState({kurslaenge:'',bildungsangebot:false,braucht_einstellungszusage:false,braucht_eignungspruefung:false,braucht_sprachtest:false,notiz:''});
  const [loading,setLoading]=useState(false);
  const [alert,setAlert]=useState(null);
  const set=(k,v)=>{
    setForm(p=>({...p,[k]:v}));
    if(k==='termin_date')terminRef.current.date=v;
    if(k==='termin_time')terminRef.current.time=v;
    if(k==='termin_berater')terminRef.current.berater=v;
  };
  const submit=async()=>{
    setLoading(true);
    try{await fetch(`/api/events/lead/${lead.lead_id}`,{method:'PATCH',headers:{'Content-Type':'application/json'},body:JSON.stringify({event_id:event.id,pd_deal_id:lead.pd_deal_id,...form})});onDone();}
    catch{setAlert({icon:'📶',title:'Verbindungsfehler',message:'Bitte nochmal versuchen.'});}
    finally{setLoading(false);}
  };
  return(
    <div className="fade-up">
      <AlertModal open={!!alert} icon={alert?.icon} title={alert?.title} message={alert?.message} onClose={()=>setAlert(null)}/>
      <div style={{display:'flex',gap:6,marginBottom:28}}>{[0,1].map(i=><div key={i} style={{flex:1,height:4,borderRadius:2,background:i===1?C.accent:C.border}}/>)}</div>
      <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:24,padding:'16px',background:C.brandLight,borderRadius:14}}>
        <div style={{width:44,height:44,borderRadius:'50%',background:C.brand,color:'white',display:'flex',alignItems:'center',justifyContent:'center',fontSize:16,fontWeight:700,flexShrink:0}}>{lead.vorname?.[0]||'?'}{lead.nachname?.[0]||''}</div>
        <div>
          <div style={{fontSize:11,fontWeight:700,color:C.accent,textTransform:'uppercase',letterSpacing:'0.07em'}}>Dozenten-Eingabe</div>
          <div style={{fontSize:18,fontWeight:800,color:C.brand}}>{lead.vorname} {lead.nachname}</div>
          <div style={{fontSize:12,color:C.muted}}>{lead.consultationType==='onsite'?'🏢 Vor-Ort-Beratung':'📞 Telefonberatung'}</div>
        </div>
      </div>
      <div style={{marginBottom:18}}>
        <div style={{fontSize:12,fontWeight:700,color:C.brand,textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:10}}>Kurslänge</div>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
          {['6 Monate','8 Monate'].map(k=>{const sel=form.kurslaenge===k;return(
            <button key={k} type="button" onClick={()=>set('kurslaenge',k)} style={{padding:'16px',borderRadius:10,fontFamily:FONT,fontSize:15,fontWeight:700,cursor:'pointer',transition:'all 150ms',background:sel?C.brand:C.white,border:`2px solid ${sel?C.brand:C.border}`,color:sel?'white':C.text}}>{k}</button>
          );})}
        </div>
      </div>
      {[['bildungsangebot','Bildungsangebot erhalten'],['braucht_einstellungszusage','Braucht Einstellungszusage'],['braucht_eignungspruefung','Braucht Eignungsprüfung'],['braucht_sprachtest','Braucht Sprachtest']].map(([k,label])=>(
        <div key={k} onClick={()=>set(k,!form[k])} style={{display:'flex',alignItems:'center',gap:12,cursor:'pointer',padding:'13px 16px',borderRadius:10,marginBottom:8,userSelect:'none',border:`1.5px solid ${form[k]?C.brand:C.border}`,background:form[k]?C.brandLight:C.white,transition:'all 130ms'}}>
          <div style={{width:20,height:20,borderRadius:5,flexShrink:0,background:form[k]?C.brand:C.white,border:`2px solid ${form[k]?C.brand:C.border}`,display:'flex',alignItems:'center',justifyContent:'center',transition:'all 130ms'}}>
            {form[k]&&<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3.5"><polyline points="20 6 9 17 4 12"/></svg>}
          </div>
          <span style={{fontSize:14,fontWeight:600,color:C.text}}>{label}</span>
        </div>
      ))}
      <div style={{marginBottom:14,marginTop:14}}>
        <div style={{fontSize:12,fontWeight:700,color:C.brand,textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:8}}>Notiz</div>
        <textarea value={form.notiz} onChange={e=>set('notiz',e.target.value)} placeholder="Freitext-Notiz (optional)..."
          style={{width:'100%',minHeight:80,padding:'12px 16px',borderRadius:10,border:`1.5px solid ${C.border}`,fontFamily:FONT,fontSize:14,resize:'vertical',outline:'none'}} />
      </div>
      <button type="button" onClick={submit} disabled={loading} style={{width:'100%',height:52,borderRadius:12,border:'none',background:loading?C.border:C.green,color:'white',fontFamily:FONT,fontSize:16,fontWeight:700,cursor:loading?'not-allowed':'pointer',marginTop:8,display:'flex',alignItems:'center',justifyContent:'center',gap:8}}>
        {loading?<><span className="spin">⟳</span> Speichern...</>:'✓ Fertig & Speichern'}
      </button>
    </div>
  );
}

// ── Step Done ─────────────────────────────────────────────────────────────────
function StepDone({onNext}){
  return(
    <div className="fade-up" style={{textAlign:'center',padding:'24px 0'}}>
      <div style={{fontSize:56,marginBottom:20}}>🎉</div>
      <h2 style={{fontSize:24,fontWeight:800,color:C.brand,marginBottom:10}}>Alles gespeichert!</h2>
      <p style={{color:C.muted,marginBottom:40}}>Lead wurde in Pipedrive angelegt und Termin eingetragen.</p>
      <button type="button" onClick={onNext} style={{width:'100%',height:52,borderRadius:12,border:'none',background:C.accent,color:'white',fontFamily:FONT,fontSize:16,fontWeight:700,cursor:'pointer',boxShadow:'0 4px 20px rgba(255,69,0,0.25)'}}>→ Nächsten Interessenten erfassen</button>
    </div>
  );
}

// ── App ───────────────────────────────────────────────────────────────────────
export default function EventsPage(){
  const [step,setStep]=useState('event');
  const [event,setEvent]=useState(null);
  const [lead,setLead]=useState(null);

  useEffect(()=>{
    document.body.style.setProperty('background','#F0F6FB','important');
    document.body.style.setProperty('background-color','#F0F6FB','important');
    document.documentElement.style.setProperty('background','#F0F6FB','important');
    const saved=loadEvent();
    if(saved){setEvent(saved);setStep('tn');}
  },[]);

  const selectEvent=ev=>{saveEvent(ev);setEvent(ev);setStep('tn');};
  const submitTN=data=>{setLead(data);setStep('handover');};
  const reset=()=>{setLead(null);setStep('tn');};

  return(
    <div style={{minHeight:'100vh',background:C.bg,fontFamily:FONT}}>
      <style>{GLOBAL}</style>
      <div style={{background:C.brand,height:54,display:'flex',alignItems:'center',justifyContent:'space-between',padding:'0 20px',position:'sticky',top:0,zIndex:50,boxShadow:'0 2px 8px rgba(0,0,0,0.15)'}}>
        <div style={{display:'flex',alignItems:'center',gap:12}}>
          <img src="https://zba.academy/wp-content/uploads/zba-logo-weiss.png" style={{height:20}} alt="ZBA"/>
          {event&&<><span style={{color:'rgba(255,255,255,0.3)'}}>|</span><span style={{fontSize:13,color:'rgba(255,255,255,0.8)',fontWeight:600,maxWidth:200,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{event.name}</span></>}
        </div>
        {event&&step!=='event'&&<button type="button" onClick={()=>{sessionStorage.removeItem(EV_KEY);setEvent(null);setStep('event');}} style={{background:'rgba(255,255,255,0.15)',border:'none',color:'rgba(255,255,255,0.85)',padding:'6px 12px',borderRadius:6,fontSize:12,fontFamily:FONT,cursor:'pointer'}}>Event wechseln</button>}
      </div>
      <div style={{maxWidth:580,margin:'0 auto',padding:'28px 20px 80px'}}>
        {step==='event'&&<StepEvent onSelect={selectEvent}/>}
        {step==='tn'&&event&&<StepTN event={event} onSubmit={submitTN}/>}
        {step==='handover'&&lead&&<StepHandover lead={lead} onContinue={()=>setStep('dozent')} onSkip={()=>setStep('done')}/>}
        {step==='dozent'&&event&&lead&&<StepDozent event={event} lead={lead} onDone={()=>setStep('done')}/>}
        {step==='done'&&<StepDone onNext={reset}/>}
      </div>
    </div>
  );
}
